import React, { useState } from "react";
import { X, Check, Sparkles, Zap } from "lucide-react";
import { createCheckout } from "../lib/api";
import { UPGRADE_REASONS, PLAN_META } from "../lib/plans";
import { toast } from "sonner";

const PLAN_FEATURES = {
  standard: ["20 orçamentos/mês", "5 modelos premium", "Sem marca Basiq", "Personalização completa", "Integração com WhatsApp"],
  pro: ["Orçamentos e modelos ilimitados", "Empresas/marcas ilimitadas", "Google Drive + envio por e-mail", "Organização automática no Drive", "Suporte prioritário"],
};

export default function UpgradeModal({ reasonKey, onClose, onSeePlans }) {
  const reason = UPGRADE_REASONS[reasonKey] || UPGRADE_REASONS.general;
  const [annual, setAnnual] = useState(true);
  const [busy, setBusy] = useState("");
  const recommended = reason.plan || "standard";
  const order = recommended === "pro" ? ["pro", "standard"] : ["standard", "pro"];

  const start = async (plan) => {
    const meta = PLAN_META[plan];
    const lookup = annual ? meta.yearly_lookup : meta.monthly_lookup;
    setBusy(plan);
    try {
      const { checkout_url } = await createCheckout(lookup);
      window.location.href = checkout_url;
    } catch {
      setBusy("");
      toast.error("Não foi possível iniciar o checkout. Tente novamente.");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4" data-testid="upgrade-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl ring-1 ring-slate-200 p-6 sm:p-7 float-up">
        <button onClick={onClose} data-testid="upgrade-close" className="absolute top-4 right-4 grid place-items-center w-8 h-8 rounded-full text-slate-400 hover:bg-slate-100 transition-colors">
          <X size={17} />
        </button>

        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 bg-slate-100 rounded-full px-2.5 py-1">
          <Sparkles size={12} /> Upgrade
        </div>
        <h2 className="mt-3 text-xl font-bold tracking-tight text-slate-900">{reason.title}</h2>
        <p className="mt-2 text-[14px] text-slate-500 leading-relaxed">{reason.desc}</p>

        <div className="mt-4 inline-flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="upgrade-billing-toggle">
          <button onClick={() => setAnnual(false)} className={`text-[12px] font-medium px-3 py-1 rounded-full transition-all ${!annual ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>Mensal</button>
          <button onClick={() => setAnnual(true)} className={`text-[12px] font-medium px-3 py-1 rounded-full transition-all inline-flex items-center gap-1.5 ${annual ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
            Anual <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">-2 meses</span>
          </button>
        </div>

        <div className="mt-4 space-y-3">
          {order.map((plan) => {
            const meta = PLAN_META[plan];
            const price = annual ? meta.yearly : meta.monthly;
            const isRec = plan === recommended;
            return (
              <div key={plan} className={`rounded-2xl p-4 ring-1 transition-all ${isRec ? "ring-2 ring-slate-900 bg-slate-50" : "ring-slate-200 bg-white"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[15px] font-bold text-slate-900">Basiq {meta.name}</span>
                    {isRec && <span className="text-[10px] font-semibold text-white bg-slate-900 rounded-full px-2 py-0.5">Recomendado</span>}
                  </div>
                  <div className="text-right leading-none">
                    <span className="text-[19px] font-extrabold text-slate-900">R${price}</span>
                    <span className="text-[11px] text-slate-400">/mês</span>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {PLAN_FEATURES[plan].slice(0, isRec ? 5 : 3).map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-[12.5px] text-slate-600">
                      <Check size={13} strokeWidth={3} className="text-slate-900 shrink-0" /> {f}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => start(plan)}
                  disabled={!!busy}
                  data-testid={`upgrade-start-${plan}`}
                  className={`mt-4 w-full h-11 rounded-full text-[14px] font-semibold inline-flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-60 ${isRec ? "bg-slate-900 text-white hover:bg-slate-800" : "bg-white ring-1 ring-slate-300 text-slate-900 hover:bg-slate-50"}`}
                >
                  {busy === plan ? "Abrindo checkout…" : <><Zap size={15} /> Testar 7 dias grátis</>}
                </button>
              </div>
            );
          })}
        </div>
        <p className="mt-4 text-center text-[11px] text-slate-400">7 dias grátis · precisa de cartão · cancele quando quiser</p>
        {onSeePlans && (
          <button onClick={onSeePlans} data-testid="upgrade-see-plans" className="mt-2 w-full text-center text-[12.5px] font-medium text-[#4A3BFA] hover:underline">
            Comparar todos os planos e ver como funciona
          </button>
        )}
      </div>
    </div>
  );
}
